import { Box, Grid, Stack, TextField, Typography } from '@mui/material'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import {
  EquipmentPlannedDto,
  StoppageReasonResponseDto,
  StoppageResponseDto,
  StoppageResponseReasonDto,
} from '@operations/app/api'
import { StoppageFormData } from '@operations/stores/StoppageStore'
import { useTranslate } from '@tolgee/react'
import { MobileViewAutocomplete, useMinimalsTheme } from '@tom-ui/ui'
import dayjs from 'dayjs'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  mapStoppageEquipmentToEquipmentPlannedDto,
  mapStoppageReasonResponseDtoToStoppageInnerDto,
  mapStoppageReasonToResponseDto,
  mapStoppageToFormData,
} from '../../helpers/mapper'
import { StoppageTime } from '../Atoms/StoppageTime'

interface IProps {
  formId: string
  vesselVisitId: number
  stoppage?: StoppageResponseDto
  stoppageReasons: StoppageReasonResponseDto[]
  equipments: EquipmentPlannedDto[]
  isVesselDeparted?: boolean
  defaultEquipmentId?: number
  setDateAutomatically?: boolean
  onSubmit: (formData: StoppageFormData) => Promise<void>
}

export const StoppageForm = observer(
  ({
    formId,
    vesselVisitId,
    stoppage,
    stoppageReasons,
    equipments,
    isVesselDeparted,
    onSubmit,
    defaultEquipmentId,
    setDateAutomatically,
  }: IProps) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const [selectedStoppageReason, setSelectedStoppageReason] =
      useState<StoppageResponseReasonDto | null>(stoppage?.stoppageReason ?? null)

    const stoppageReasonsWithCurrentSelected = useMemo(() => {
      let list = [...stoppageReasons]
      if (!!stoppage && !stoppageReasons.some(x => x.id === stoppage.stoppageReason.id))
        list = [...stoppageReasons, mapStoppageReasonToResponseDto(stoppage.stoppageReason)]

      return _(list)
        .uniqBy(x => x.id)
        .sortBy(x => x.name)
        .value()
    }, [stoppage, stoppageReasons])

    const equipmentsWithCurrentSelected = useMemo(() => {
      let list = [...equipments]
      if (!!stoppage && stoppage.equipments.length > 0) {
        list = [
          ...equipments,
          ...stoppage.equipments.map(e => mapStoppageEquipmentToEquipmentPlannedDto(e)),
        ]
      }

      return _(list)
        .uniqBy(x => x.id)
        .sortBy(x => x.name)
        .value()
    }, [equipments, stoppage])

    const defaultValues = mapStoppageToFormData(vesselVisitId)

    if (defaultEquipmentId) defaultValues.equipmentIds = [defaultEquipmentId]
    if (setDateAutomatically) {
      const currentTime = dayjs().toISOString()
      defaultValues.fromDate = currentTime
      defaultValues.fromTime = currentTime
    }

    const {
      control,
      handleSubmit,
      reset,
      watch,
      formState: { errors },
    } = useForm<StoppageFormData>({
      defaultValues: defaultValues,
    })

    useEffect(() => {
      const data = mapStoppageToFormData(vesselVisitId, stoppage)
      if (setDateAutomatically && !!stoppage?.from && !stoppage?.to) {
        const currentTime = dayjs().toISOString()
        data.toDate = currentTime
        data.toTime = currentTime
      }
      if (stoppage) reset(data)
    }, [reset, setDateAutomatically, stoppage, vesselVisitId])

    return (
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack gap={theme.customSpacing.m}>
          <Typography variant='subtitle2'>{t('dateAndTime', 'Date and time')}</Typography>

          <Grid container spacing={theme.customSpacing.xs}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                control={control}
                name='fromDate'
                rules={{ required: true }}
                render={({ field }) => (
                  <Box data-cy={'stoppage-from-date-input'}>
                    <DatePicker
                      {...field}
                      value={field.value ? dayjs(field.value) : null}
                      label={t('startDate', 'Start date')}
                      slotProps={{
                        textField: {
                          required: true,
                          helperText: errors.fromDate
                            ? t('fieldIsRequired', 'Field is required.')
                            : '',
                          error: !!errors.fromDate,
                          fullWidth: true,
                        },
                      }}
                    />
                  </Box>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                control={control}
                name='fromTime'
                rules={{ required: true }}
                render={({ field }) => (
                  <Box data-cy={'stoppage-from-time-input'}>
                    <TimePicker
                      {...field}
                      ampm={false}
                      value={field.value ? dayjs(field.value) : null}
                      label={t('startTime', 'Start time')}
                      slotProps={{
                        textField: {
                          required: true,
                          helperText: errors.fromTime
                            ? t('fieldIsRequired', 'Field is required.')
                            : '',
                          error: !!errors.fromTime,
                          fullWidth: true,
                        },
                      }}
                    />
                  </Box>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                control={control}
                name='toDate'
                rules={{ required: isVesselDeparted || !!watch('toTime') }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    value={field.value ? dayjs(field.value) : null}
                    label={t('endDate', 'End date')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: isVesselDeparted || !!watch('toTime'),
                        helperText: errors.toDate ? t('fieldIsRequired', 'Field is required.') : '',
                        error: !!errors.toDate,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Controller
                control={control}
                name='toTime'
                rules={{ required: isVesselDeparted }}
                render={({ field }) => (
                  <TimePicker
                    {...field}
                    ampm={false}
                    value={field.value ? dayjs(field.value) : null}
                    label={t('endTime', 'End time')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: isVesselDeparted,
                        helperText: errors.toTime ? t('fieldIsRequired', 'Field is required.') : '',
                        error: !!errors.toTime,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid size={12}>
            <StoppageTime
              fromDate={watch('fromDate')}
              fromTime={watch('fromTime')}
              toDate={watch('toDate')}
              toTime={watch('toTime')}
            />
          </Grid>

          <Stack gap={theme.customSpacing.xs}>
            <Typography variant='subtitle2'>
              {t('stoppageOverview', 'Stoppage overview')}
            </Typography>

            <Controller
              control={control}
              name='stoppageReasonId'
              rules={{ required: true }}
              render={({ field }) => (
                <MobileViewAutocomplete
                  {...field}
                  options={stoppageReasonsWithCurrentSelected}
                  value={stoppageReasonsWithCurrentSelected.find(x => x.id === field.value) ?? null}
                  getOptionLabel={option => option.name}
                  onChange={(_, value) => {
                    field.onChange(value?.id ?? null)
                    setSelectedStoppageReason(
                      value ? mapStoppageReasonResponseDtoToStoppageInnerDto(value) : null,
                    )
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      key={params.id}
                      label={t('name', 'Name')}
                      required
                      error={!!errors.stoppageReasonId}
                      helperText={
                        errors['stoppageReasonId']
                          ? t('fieldIsRequired', 'Field is required')
                          : undefined
                      }
                      data-cy='stoppage-reason-autocomplete'
                    />
                  )}
                />
              )}
            />

            <TextField
              label={t('reason', 'Reason')}
              value={selectedStoppageReason ? t(selectedStoppageReason.type) : ''}
              fullWidth
              disabled
            />

            <TextField
              label={t('responsible', 'Responsible')}
              value={selectedStoppageReason ? t(selectedStoppageReason.responsible) : ''}
              fullWidth
              disabled
            />

            <Controller
              control={control}
              name='equipmentIds'
              render={({ field }) => (
                <MobileViewAutocomplete
                  {...field}
                  multiple
                  options={equipmentsWithCurrentSelected}
                  value={
                    equipmentsWithCurrentSelected.filter(x => field.value?.includes(x.id)) ?? []
                  }
                  getOptionLabel={option => option.name}
                  onChange={(_, newValue: EquipmentPlannedDto[] | null) =>
                    field.onChange(newValue?.map(x => x.id) ?? [])
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      key={params.id}
                      label={t('crane', 'Crane')}
                      helperText={t(
                        'toStopTheWholeVisitDontSelectAnyCranes',
                        "If you want to stop the whole visit, don't select any cranes.",
                      )}
                    />
                  )}
                />
              )}
            />
          </Stack>

          <Stack gap={theme.customSpacing.xs}>
            <Typography variant='subtitle2'>{t('notes', 'Notes')}</Typography>
            <Controller
              control={control}
              name='notes'
              render={({ field }) => (
                <TextField {...field} label={t('notes', 'Notes')} fullWidth minRows={3} multiline />
              )}
            />
          </Stack>
        </Stack>
      </form>
    )
  },
)
